import { TableMap } from "@tiptap/pm/tables";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { NumberInput } from "@/components/sidebar/NumberInput";
import { ActiveTooltip, TooltipShortcut } from "@/components/tooltip/Tooltip";
import { displayHotkey } from "@/utils/hotkeys";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeWithPos } from "@tiptap/core";

type Props = {
  doc: DocumentModel;
  tableNode: NodeWithPos;
};

export const TableDimensions = observer(function TableDimensions({ tableNode, doc }: Props) {
  const t = useTranslations();
  const table = TableMap.get(tableNode.node);
  const { tiptap } = doc.editor;

  const onRowsChange = (value: number) => {
    if (!tiptap) {
      return;
    }

    const rowsToAdd = value - table.height;

    if (rowsToAdd > 0) {
      tiptap.chain().appendRow(rowsToAdd).focus().run();
    } else {
      tiptap
        .chain()
        .popRow(rowsToAdd * -1)
        .focus()
        .run();
    }
  };

  const onColsChange = (value: number) => {
    if (!tiptap) {
      return;
    }

    const colsToAdd = value - table.width;

    if (colsToAdd > 0) {
      tiptap.chain().appendColumn(colsToAdd).focus().run();
    } else {
      tiptap
        .chain()
        .popColumn(colsToAdd * -1)
        .focus()
        .run();
    }
  };

  return (
    <div className="flex gap-1">
      <ActiveTooltip
        focusDisabled
        title={
          <>
            {t("sidebar.actions.addRow")}
            <TooltipShortcut>{displayHotkey("add-row")}</TooltipShortcut>
          </>
        }
      >
        <NumberInput
          value={table.height}
          min={1}
          max={500}
          className="max-w-[76px]"
          icon={<Icon name="TableRows" />}
          aria-label={t("sidebar.actions.addRow")}
          onSubmit={onRowsChange}
        />
      </ActiveTooltip>
      <ActiveTooltip
        focusDisabled
        title={
          <>
            {t("sidebar.actions.addColumn")}
            <TooltipShortcut>{displayHotkey("add-column")}</TooltipShortcut>
          </>
        }
      >
        <NumberInput
          value={table.width}
          min={1}
          max={100}
          className="max-w-[76px]"
          icon={<Icon name="TableCols" />}
          aria-label={t("sidebar.actions.addColumn")}
          onSubmit={onColsChange}
        />
      </ActiveTooltip>
    </div>
  );
});
